import { AgGridReact } from "ag-grid-react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  applyUnassignedDefaults,
  flattenData,
  translateVLargeMaterial
} from "pages/reports-menu/project-reports/feature-custom-reports/utils/utils";
import { ProjectContext } from "context/ProjectProvider";
import useReportColumnDef from "pages/reports-menu/project-reports/feature-custom-reports/hooks/report-column-def";
import { useParams } from "react-router";
import { useEnumValues } from "hooks/enum-values";
import { FeatureTypes } from "entity-app/constants";
import { getCustomReportData } from "pages/reports-menu/project-reports/feature-custom-reports/services/custom-report-services";
import { fetchCommentsByReportRowId } from "pages/reports-menu/project-reports/feature-custom-reports/custom-report-comments";
import { CustomReportPrintViewState } from "./model";
import AgGridPrintLayout from "./ag-grid-print-layout";

/** Renders nothing, but its marker element lets print CSS mask the row
 * separator above the cell so the group still reads as one merged block.
 * Emitted by the renderer rather than tagged via cellClass, which AG Grid
 * evaluates before its post-render relayout and so leaves misaligned. */
function BlankSpanContinuationCell() {
  return <div className="print-span-continuation" />;
}

// AG Grid renders spanned cells as absolutely positioned boxes, which Chrome
// cannot fragment across printed pages — the whole spanning layer lands on one
// page and every other page loses those columns. Print drops the span and emits
// the value on the group's first displayed row instead; continuation rows carry
// the same material fields, so blanking them loses nothing. A shadow row whose
// material row above was filtered out or sorted away still shows its values.
const continuesPreviousRow = (params: any): boolean => {
  if (!params.data?.shodowRow) return false;
  const rowIndex = params.node?.rowIndex;
  if (rowIndex == null || !params.api) return true;
  const previous = params.api.getDisplayedRowAtIndex(rowIndex - 1);
  return previous?.data?.id === params.data.id;
};

const unspanColumnForPrint = (col: any): any => {
  if (col?.children?.length) {
    return { ...col, children: col.children.map(unspanColumnForPrint) };
  }
  if (!col?.spanRows) return col;

  const originalSelector = col.cellRendererSelector;
  const rest = { ...col };
  delete rest.spanRows;

  return {
    ...rest,
    cellRendererSelector: (params: any) =>
      continuesPreviousRow(params)
        ? { component: BlankSpanContinuationCell }
        : originalSelector?.(params)
  };
};

export default function CustomReportPrintView({
  state
}: {
  state: CustomReportPrintViewState;
}) {
  const gridRef = useRef<AgGridReact<any>>(null);
  const { projectToken, isFeatureFlagEnabled, columnHeaders } =
    useContext(ProjectContext);
  const materialCustomColumns = columnHeaders?.materialCustomColumns;
  const { enumOptionsMap } = useEnumValues(
    FeatureTypes.MATERIALS,
    !materialCustomColumns?.length
  );

  const { projectId } = useParams() as { projectId: string };
  const [vLargeMaterial, setVLargeMaterial] = useState<any[] | null>(null);
  const [isVLargeMaterialLoading, setIsVLargeMaterialLoading] = useState(false);

  const isLBSFeatureEnabled = isFeatureFlagEnabled(
    "LOCATION_BREAKDOWN_STRUCTURE"
  );
  const isMaterialCsiClassificationEnabled = isFeatureFlagEnabled(
    "MATERIAL_CSI_CLASSIFICATION"
  );

  const getReportData = useCallback(async () => {
    if (!state.reportId || !projectToken) return;

    const [response, commentsById] = await Promise.all([
      getCustomReportData(state.reportId, projectToken),
      state.includeComments
        ? fetchCommentsByReportRowId(state.reportId, projectToken)
        : null
    ]);
    const data = response?.data || null;
    setVLargeMaterial(
      data && commentsById
        ? data.map((row: any) => ({
            ...row,
            comments: commentsById.get(row.id) ?? []
          }))
        : data
    );
    setIsVLargeMaterialLoading(false);
  }, [state.reportId, state.includeComments, projectToken]);

  useEffect(() => {
    getReportData();
  }, [getReportData]);

  const materials = useMemo(() => {
    if (!vLargeMaterial) return null;
    return translateVLargeMaterial(vLargeMaterial);
  }, [vLargeMaterial]);

  const rowData = useMemo(() => {
    if (
      !state?.datasetConfig?.linkedEntity ||
      !materials ||
      isVLargeMaterialLoading
    )
      return null;

    let opMaterials = [...materials];

    const inlineModeForFeatures = state?.datasetConfig?.linkedEntity
      .filter((entity) => entity.display_type === "inline")
      .reduce((acc, entity) => {
        acc.push(entity.feature_type_id);
        return acc;
      }, [] as number[]);

    opMaterials = applyUnassignedDefaults(
      opMaterials,
      state?.datasetConfig?.groupByField,
      state?.columnConfig ?? null
    );

    opMaterials = flattenData(opMaterials, inlineModeForFeatures);

    return opMaterials;
  }, [
    isVLargeMaterialLoading,
    materials,
    state?.columnConfig,
    state?.datasetConfig?.groupByField,
    state?.datasetConfig?.linkedEntity
  ]);

  const { columnDefs, autoGroupColumnDef } = useReportColumnDef({
    linkedEntityDisplayType: state?.datasetConfig?.linkedEntity ?? [],
    groupByField: state?.datasetConfig?.groupByField ?? "none",
    isLBSFeatureEnabled: isLBSFeatureEnabled ?? false,
    isMaterialCsiClassificationEnabled,
    projectId: projectId ?? "",
    materialCustomColumns,
    enumOptionsMap
  } as any);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [enumOptionsMap]);

  const printColumnDefs = useMemo(
    () => (columnDefs ?? []).map(unspanColumnForPrint),
    [columnDefs]
  );

  const printAutoGroupColumnDef = useMemo(
    () =>
      autoGroupColumnDef ? unspanColumnForPrint(autoGroupColumnDef) : undefined,
    [autoGroupColumnDef]
  );

  const printState = useMemo(
    () => ({ ...state, enableCellSpan: false }),
    [state]
  );

  return (
    <AgGridPrintLayout
      gridRef={gridRef}
      state={printState}
      columnDefEntity={printColumnDefs}
      autoGroupColumnDef={printAutoGroupColumnDef}
      rowData={rowData}
    />
  );
}
